import React, { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { Check, CheckCircle2, Download, Loader2 } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import workflowRegistry, { getWorkflowEntry } from "./workflowRegistry";
import WorkflowFlow from "./WorkflowFlow";
import { addWorkflowComponent, downloadDataUrl, listWorkflowComponents, removeWorkflowComponent, slugify } from "./imageCreationService";
import { useWorkflowState } from "./WorkflowStateContext";

export default function WorkflowSection({ onGenerated }) {
  const [tab, setTab] = useState("components");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingType, setAddingType] = useState("");
  // Items sent from the Menu Data Extraction card, waiting to be loaded into
  // the Image Creation card's form. Lives here (their common parent) so the
  // two cards stay independent components while still talking to each other.
  const [pendingMenuItems, setPendingMenuItems] = useState([]);
  // Same idea for enhanced product images sent from the Image Extraction card.
  const [pendingImages, setPendingImages] = useState([]);
  // Products (and the menu PDF) sent from the Menu Data Extraction card, waiting for the Image Extraction card.
  const [pendingProducts, setPendingProducts] = useState([]);
  // What was last handed on between steps, for the Workflow tab's live status (names only; the data itself travels in the queues above).
  const [handoff, setHandoff] = useState({ product: null, image: null, extraction: null });
  const { extractedSections, imageVersions, imageSelectedKey } = useWorkflowState();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listWorkflowComponents()
      .then((list) => setItems(list.filter((c) => getWorkflowEntry(c.type))))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
  }, [load]);

  const handleAdd = async (type) => {
    setAddingType(type);
    try {
      const added = await addWorkflowComponent(type);
      setItems((prev) => (prev.some((c) => c.id === added.id) ? prev : [...prev, added]));
      setTab("components");
      message.success(`${getWorkflowEntry(type)?.name || "Component"} added to Components.`);
    } catch (err) {
      message.error(err.message);
    } finally {
      setAddingType("");
    }
  };

  const handleRemove = async (item) => {
    const name = getWorkflowEntry(item.type)?.name || "this item";
    if (!window.confirm(`Remove ${name} from your Components? Your images and saved prompts are kept.`)) return;
    try {
      await removeWorkflowComponent(item.id);
      setItems((prev) => prev.filter((c) => c.id !== item.id));
    } catch (err) {
      message.error(err.message);
    }
  };

  // Connect the cards through the shared queues above, without any of them knowing about the others.
  // `present` = the set of component types available to hand results to (Components tab: added ones; Workflow tab: the flow's steps).
  const bridgePropsFor = (type, present, { workflow = false } = {}) =>
    type === "image-creation"
      ? {
          pendingItems: pendingMenuItems,
          onConsumePendingItem: () => setPendingMenuItems((prev) => prev.slice(1)),
          pendingImages,
          onConsumePendingImage: () => setPendingImages((prev) => prev.slice(1)),
        }
      : type === "menu-data-extraction"
        ? {
            onSendToImageCreation: (list) => setPendingMenuItems((prev) => [...prev, ...list]),
            hasImageCreationCard: present.has("image-creation"),
            onSendToImageExtraction: (product) => {
              setPendingProducts((prev) => [...prev, product]);
              setHandoff((h) => ({ ...h, product: { name: product.name, hasPdf: !!product.file }, extraction: null }));
            },
            hasImageExtractionCard: present.has("image-extraction"),
          }
        : type === "image-extraction"
          ? {
              onSendToImageCreation: (list) => {
                setPendingImages((prev) => [...prev, ...list]);
                if (list[0]) setHandoff((h) => ({ ...h, image: { name: list[0].name, price: list[0].price, description: list[0].description } }));
              },
              hasImageCreationCard: present.has("image-creation"),
              pendingProducts,
              onConsumePendingProduct: () => setPendingProducts((prev) => prev.slice(1)),
              ...(workflow ? { autoSendToNext: true, onProgress: (e) => setHandoff((h) => ({ ...h, extraction: e })) } : {}),
            }
          : {};

  // ----- Workflow tab: live status of each step, from the real data -----
  const menuCount = (extractedSections || []).reduce((n, sec) => n + (sec.items || []).length, 0);
  const createdImage = imageVersions.find((v) => v.key === imageSelectedKey) || imageVersions[0] || null;
  const flowStatus = {
    "menu-data-extraction":
      menuCount > 0
        ? { tone: "ok", text: `${menuCount} item${menuCount === 1 ? "" : "s"} extracted. Pick one and press “Send to Image Extraction”` }
        : { tone: "idle", text: "Upload a menu PDF or photo and extract it" },
    "image-extraction": (() => {
      const e = handoff.extraction;
      if (e?.type === "found") return { tone: "ok", text: `Found “${e.name}”${e.detail ? ` in ${e.detail}` : ""} and sent it on` };
      if (e?.type === "not-found") return { tone: "warn", text: `No picture of “${e.name}” in ${e.detail}. Check the name or upload another PDF` };
      if (e?.type === "no-pdf") return { tone: "warn", text: `“${e.name}” received, but there is no PDF yet. Upload the PDF with its picture` };
      if (e?.type === "error") return { tone: "warn", text: `Couldn't read the PDF: ${e.detail}` };
      if (handoff.product) return { tone: "idle", text: `Looking for “${handoff.product.name}”${handoff.product.hasPdf ? " in the menu PDF" : ""}…` };
      return { tone: "idle", text: "Waiting for an item from Menu Data Extraction (or type a product name)" };
    })(),
    "image-creation": createdImage
      ? { tone: "ok", text: "Image created" }
      : handoff.image
        ? { tone: "idle", text: `Received the picture of “${handoff.image.name}”${handoff.image.price ? " with price" : ""}. Add a template image, then Generate` }
        : { tone: "idle", text: "Waiting for the extracted picture, and a template image from you" },
  };
  const flowFinal = (
    <div className="flex flex-col gap-2 rounded-2xl border border-green-200 bg-green-50/50 p-4" data-testid="final-result">
      <div className="flex items-center gap-2">
        <CheckCircle2 size={18} className="text-green-600" />
        <h3 className="m-0 text-sm font-semibold text-gray-900">Final result</h3>
      </div>
      {createdImage ? (
        <div className="flex flex-col gap-2">
          <img src={createdImage.imageDataUrl} alt={createdImage.title || "Final result"} className="max-h-80 w-full rounded-lg bg-white object-contain" />
          <button
            type="button"
            onClick={() => downloadDataUrl(createdImage.imageDataUrl, `${slugify(createdImage.title) || "menu-image"}.png`)}
            className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <Download size={14} /> Download PNG
          </button>
        </div>
      ) : (
        <p className="m-0 text-sm text-gray-600">Your finished image appears here once Image Creation has generated it.</p>
      )}
    </div>
  );
  const renderFlowStep = (type, present) => {
    const entry = getWorkflowEntry(type);
    if (!entry) return null;
    const Component = entry.component;
    // In the Workflow tab, `present` = the steps currently in the flow.
    return <Component key={type} onGenerated={onGenerated} {...bridgePropsFor(type, present, { workflow: true })} />;
  };

  const components = items;
  const added = new Set(items.map((c) => c.type));
  const tabCls = (active) =>
    `rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"}`;

  return (
    <section aria-labelledby="components-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="components-heading" className="text-lg font-bold text-gray-900">Components &amp; Workflow</h2>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "components"} className={tabCls(tab === "components")} onClick={() => setTab("components")}>
            Components
          </button>
          <button type="button" role="tab" aria-selected={tab === "library"} className={tabCls(tab === "library")} onClick={() => setTab("library")}>
            Library
          </button>
          <button type="button" role="tab" aria-selected={tab === "workflow"} className={tabCls(tab === "workflow")} onClick={() => setTab("workflow")}>
            Workflow
          </button>
        </div>
      </div>

      {tab === "components" && (
        <div className="flex flex-col gap-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Loading your components…
            </div>
          ) : error ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" onClick={load} className="font-semibold underline">Try again</button>
            </div>
          ) : components.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 px-4 py-10 text-center">
              <p className="font-semibold text-gray-900">Your Components list is empty</p>
              <p className="text-sm text-gray-500">Add tools like Image Creation from the Library.</p>
              <button type="button" onClick={() => setTab("library")} className="mt-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                Open Library
              </button>
            </div>
          ) : (
            components.map((c) => {
              const entry = getWorkflowEntry(c.type);
              if (!entry) return null;
              const Component = entry.component;
              const bridgeProps = bridgePropsFor(c.type, added);
              return (
                <Component key={c.id} onGenerated={onGenerated} onRemove={() => handleRemove(c)} {...bridgeProps} />
              );
            })
          )}
        </div>
      )}

      {tab === "workflow" && <WorkflowFlow renderStep={renderFlowStep} status={flowStatus} finalResult={flowFinal} />}

      {tab === "library" && (
        <div className="flex flex-col gap-6">
          {[
            { key: "components", title: "Components", entries: workflowRegistry, addedLabel: "In Components", addLabel: "Add to Components" },
          ].map((group) => (
            <div key={group.key} className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{group.title}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.entries.map((entry) => {
                  const Icon = entry.icon;
                  const isAdded = added.has(entry.type);
                  return (
                    <div key={entry.type} className="flex flex-col items-center gap-2 rounded-2xl border border-gray-200 bg-white p-5 text-center">
                      <div className="grid h-11 w-11 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                        <Icon size={22} />
                      </div>
                      <h4 className="text-base font-semibold text-gray-900">{entry.name}</h4>
                      <p className="text-sm text-gray-600">{entry.description}</p>
                      {isAdded ? (
                        <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
                          <Check size={15} /> {group.addedLabel}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAdd(entry.type)}
                          disabled={!!addingType || loading}
                          className="mt-1 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {addingType === entry.type && <Loader2 size={14} className="animate-spin" />}
                          {group.addLabel}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
